import React from 'react';
import { PageBanner } from '../components/PageBanner';
export function ChildProtectionPage() {
  return <div className="pb-24" data-id="element-246">
      <PageBanner title="Child Protection at Firetree" subtitle="Firetree brings over four decades of direct experience to child protection—as founders, board members, operators, and funders." image="https://cdn.magicpatterns.com/uploads/4imhJsDmHTb7rc8urHv2wf/Baan_Dek_Foundation_3.jpg" credit="Photo: Baan Dek Foundation" data-id="element-247" />

      {/* How child protection works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-248">
        <div className="max-w-4xl" data-id="element-249">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-8" data-id="element-250">
            How child protection works
          </h2>
          <div className="space-y-6 text-lg text-brand-brown leading-relaxed" data-id="element-251">
            <p data-id="element-252">
              Child protection is often described in terms of individual
              services—rescue, shelter, counselling, education. But effective
              protection is better understood as a cycle, with multiple services
              running in parallel throughout.
            </p>
            <p data-id="element-253">
              Mith Samlanh, Friends-International's program in Cambodia,
              developed a model that captures this well. A child's journey may
              begin with outreach—being identified in the community as at risk.
              From there, they may access protection services, drop-in centers,
              harm-reduction, or healthcare for immediate stabilization. Some
              need transitional housing while longer-term plans develop.
              Education and training build the skills for independence. The
              immediate goal is safety, the long-term one is reintegration—back
              to school, into employment, with family or foster care, as a
              functioning adult, parent and member of the community.
            </p>
            <p data-id="element-254">
              But the process is non-linear. A child reintegrated into family
              may later need to return to transitional care. Someone in
              education may face a crisis requiring acute intervention. The
              cycle reflects reality: progress isn't linear, and protection
              systems must accommodate setbacks without treating them as
              failures.
            </p>
            <p data-id="element-255">
              Crucially, certain services operate throughout the entire process,
              regardless of where a child sits in the cycle: protection of
              rights and participation, life skills education, HIV/AIDS
              prevention and care, and medical services. These aren't stages to
              pass through—they're constant layers of support.
            </p>

            <div className="my-10 p-8 bg-brand-card/30 border-l-4 border-brand-gold rounded-r-2xl" data-id="element-256">
              <p className="font-medium text-brand-charcoal" data-id="element-257">
                Each child is treated as an individual. The model provides a
                framework, but no two children follow the same path through it.
                A child's specific needs, circumstances, fears, and aspirations
                determine which services they access and in what sequence. This
                requires tailored case planning—understanding what this
                particular child requires, not applying a standard package. Some
                children need intensive therapeutic support; others need
                documentation to access school. Some are ready for vocational
                training; others must first address family situations that put
                them at risk. The cycle is the same; the journey through it is
                unique to each child.
              </p>
            </div>

            <p data-id="element-258">
              This model reflects how we approach the work at Firetree. TCI's
              structure—with specialized units for community social work, sexual
              abuse cases, and alternative care, alongside community programs
              for women and youth—mirrors the same logic. Our partners operate
              similarly: M'Lop Tapang's multidisciplinary teams, Baan Dek's
              three pillars, Stairway's integration of direct services with
              national advocacy. We all recognize that child protection requires
              both a pathway (outreach to reintegration) and a foundation
              (rights, health, skills) that never drops away—and that each child
              moving through that pathway needs a plan built around them, not
              around our programs.
            </p>
          </div>
        </div>
      </section>

      {/* Child protection as trafficking prevention */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-259">
        <div className="max-w-4xl" data-id="element-260">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-8" data-id="element-261">
            Child protection as trafficking prevention
          </h2>
          <div className="space-y-6 text-lg text-brand-brown leading-relaxed" data-id="element-262">
            <p data-id="element-263">
              Anti-trafficking has become a donor buzzword, often
              sensationalized at the expense of substance. Firetree's partners
              choose not to use the label— yet child protection work, done the
              way we have described, is essential to preventing human
              trafficking.
            </p>
            <p data-id="element-264">
              The children and youth served—those facing abuse, neglect, family
              breakdown, lack of documentation, or economic desperation—are
              precisely those most vulnerable to traffickers. A child without a
              birth certificate cannot prove their age or citizenship. A
              teenager estranged from family has no safety net. A young person
              without skills or employment options is susceptible to false
              promises of work.
            </p>
            <p data-id="element-265">
              Every element of the protection cycle reduces trafficking risk:
              outreach identifies vulnerable children before traffickers do;
              stabilization and safe housing remove them from dangerous
              situations; education and vocational training create legitimate
              pathways to independence; family strengthening rebuilds the
              support systems that make exploitation less likely; documentation
              gives children legal identity and access to protection under the
              law.
            </p>

            <div className="my-10 p-8 bg-brand-card/30 border-l-4 border-brand-gold rounded-r-2xl" data-id="element-266">
              <p className="font-medium text-brand-charcoal" data-id="element-267">
                This is prevention at its most practical. Rather than addressing
                trafficking after the fact, effective child protection
                intervenes upstream—building the resilience, options, and
                support structures that make children less vulnerable in the
                first place.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our expertise */}
      <section className="py-24 bg-brand-card/30" data-id="element-268">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-269">
          <div className="max-w-4xl" data-id="element-270">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-8" data-id="element-271">
              Our expertise
            </h2>
            <div className="space-y-6 text-lg text-brand-brown leading-relaxed" data-id="element-272">
              <p data-id="element-273">
                Firetree's leadership has been immersed in child protection work
                across Asia for decades. Francesco Caruso, Firetree's Principal,
                has co-founded child protection organizations and served on
                multiple boards in the sector. Nicky Wilkinson has worked within
                child protection organizations and continues to sit on several
                boards.
              </p>
              <p data-id="element-274">
                We also employ Nancy Agaid, a child protection specialist with
                over 40 years of experience, as our senior technical advisor.
                Nancy provides direct oversight to our operations and brings the
                kind of deep practice knowledge that only comes from decades of
                frontline and leadership work in the field.
              </p>
              <p className="font-medium text-brand-charcoal" data-id="element-275">
                This isn't grantmaking at arm's length—it reflects careers spent
                understanding what effective protection requires.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What we operate */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-276">
        <div className="max-w-4xl" data-id="element-277">
          <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-8" data-id="element-278">
            What we operate
          </h2>
          <div className="space-y-6 text-lg text-brand-brown leading-relaxed" data-id="element-279">
            <p data-id="element-280">
              Tondo Community Initiative (TCI) in Manila is Firetree's own child
              protection program, with Francesco serving as President and Nancy
              as senior technical advisor. TCI works in Tondo—home to the former
              Smokey Mountain landfill—one of the Philippines' most marginalized
              urban communities.
            </p>
            <p data-id="element-281">
              We employ 16 licensed social workers who provide crisis
              intervention for children facing sexual abuse, severe neglect,
              abandonment, or life-threatening situations. Our teams are
              organized into specialized units: a Community Social Work Team
              that identifies and responds to emergencies, a Child Protection
              Unit focused on sexual abuse cases, and an Alternative Care Unit
              that finds safe placements when children cannot remain with their
              families. We have opened a Crisis Center to provide short-term
              protective care while longer-term solutions are developed.
            </p>
            <p data-id="element-282">
              Beyond crisis response, TCI runs community programs that build
              resilience over time: a Women's Forum that develops mothers'
              leadership and economic agency, a Youth Program designed around
              young people's own aspirations, and efforts to secure birth
              certificates so families can access public services. We partner
              with other organizations for education, vocational training, and
              healthcare—contributing the specialized social work capacity that
              many lack resources to maintain.
            </p>
          </div>
        </div>
      </section>

      {/* Why practitioner-led funding matters */}
      <section className="py-24 bg-brand-charcoal text-brand-cream" data-id="element-283">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-284">
          <div className="max-w-4xl" data-id="element-285">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-gold mb-8" data-id="element-286">
              Why practitioner-led funding matters
            </h2>
            <div className="space-y-6 text-lg text-brand-shape leading-relaxed" data-id="element-287">
              <p data-id="element-288">
                Child protection is work that requires a combination of strong
                professional skills and empathy. The difference between adequate
                and excellent practice—in case management, in trauma-informed
                care, in navigating legal systems, in knowing when family
                reunification is safe and when it isn't—is difficult to assess
                from the outside. Organizations that only fund this work can
                struggle to evaluate quality.
              </p>
              <p data-id="element-289">
                Because we operate TCI, we evaluate partners against what we
                know from direct experience. We can assess whether case
                management is rigorous, whether organizations genuinely center
                children's agency, whether they understand the relationship
                between crisis intervention and longer-term development. Our
                work and board experience across multiple organizations gives us
                pattern recognition that pure funders lack.
              </p>
              <p data-id="element-290">
                The relationship works both ways. TCI learns from M'Lop Tapang's
                community-based protection networks, from Baan Dek's private
                sector engagement, from Friends-International's coordination and
                advocacy models, from Stairway's national training
                infrastructure, from Yayasan Chow Kit's work with stateless and
                undocumented children. We are practitioners in conversation with
                practitioners.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}